import apiService from './api';
import type { Numeric, NumericValue } from '@/utils/numeric';

export interface WeatherSnapshot {
  temp_c: NumericValue;
  wind_kph: NumericValue;
  wind_dir: string;
  humidity: Numeric;
  precip_mm: number | null;
  conditions: string;
  fetched_at: string;
}

export interface ForecastHour {
  time: string;
  temp_c: Numeric;
  humidity: Numeric;
  wind_kph: Numeric;
  wind_dir: string | null;
  precip_mm: number | null;
  conditions: string;
  code: number | null;
}

export interface ForecastDay {
  date: string;
  temp_min: number | null;
  temp_max: number | null;
  precip_mm: number | null;
  wind_max_kph: number | null;
  uv_max: number | null;
  conditions: string;
  code: number | null;
}

export interface ForecastRange {
  hourly: ForecastHour[];
  daily: ForecastDay[];
  fetched_at: string;
}

/**
 * Open-Meteo model estimate of current conditions, shaped like a station
 * observation so the same tiles render either. Not a measurement.
 */
export interface ModelCurrentConditions {
  observed_at: string;
  temp_c: number | null;
  humidity: number | null;
  feels_like_c: number | null;
  dew_point_c: number | null;
  wind_kph: number | null;
  wind_dir: number | null;
  wind_gust_kph: number | null;
  rain_mm_hour: number | null;
  rain_mm_day: number | null;
  solar_wm2: number | null;
  pressure_hpa: number | null;
  uv: number | null;
  conditions: string;
  code: number | null;
  grid_elevation_m: number | null;
  fetched_at: string;
  source: 'open_meteo';
}

export interface FarmWeatherOverview {
  farm_id: number;
  farm_name: string;
  lat: number | null;
  lng: number | null;
  station_id: number | null;
  station_name: string | null;
  source: 'station' | 'forecast' | 'unavailable';
  snapshot: WeatherSnapshot | null;
}

class WeatherService {
  async getForecast(lat: number, lng: number, time?: string): Promise<WeatherSnapshot | null> {
    try {
      const params = new URLSearchParams();
      params.set('lat', String(lat));
      params.set('lng', String(lng));
      if (time) params.set('time', time);
      const res = await apiService.get<WeatherSnapshot>(`/weather/forecast?${params.toString()}`);
      if (res && res.success && res.data) return res.data;
      return null;
    } catch {
      return null;
    }
  }

  async getFarmsOverview(): Promise<FarmWeatherOverview[] | null> {
    try {
      const res = await apiService.get<FarmWeatherOverview[]>(`/weather/farms-overview`);
      if (res && res.success && res.data) return res.data;
      return null;
    } catch {
      return null;
    }
  }

  async getCurrent(lat: number, lng: number): Promise<ModelCurrentConditions | null> {
    try {
      const params = new URLSearchParams();
      params.set('lat', String(lat));
      params.set('lng', String(lng));
      const res = await apiService.get<ModelCurrentConditions>(`/weather/current?${params.toString()}`);
      if (res && res.success && res.data) return res.data;
      return null;
    } catch {
      return null;
    }
  }

  async getForecastRange(lat: number, lng: number, days = 7): Promise<ForecastRange | null> {
    try {
      const params = new URLSearchParams();
      params.set('lat', String(lat));
      params.set('lng', String(lng));
      params.set('days', String(days));
      const res = await apiService.get<ForecastRange>(`/weather/forecast-range?${params.toString()}`);
      if (res && res.success && res.data) return res.data;
      return null;
    } catch {
      return null;
    }
  }
}

export const weatherService = new WeatherService();
export default weatherService;
